import { supabase } from '../supabaseClient';
import {
  AnnualTimetableCell,
  ProgramFixedContentRow,
  ProgramProgressCell,
  ProgramProgressCellUpsert,
} from '../types/domain';
import type { ProgramMutationNotifyOptions } from './programFixedContentService';
import {
  deleteProgramFixedContentRowsByIds,
  upsertProgramFixedContentRows,
} from './programFixedContentService';
import {
  cleanupPlanHasWork,
  planFixedProgramUnfixedSlotCleanup,
} from '../utils/fixedProgramUnfixedSlotCleanup';
import { buildAnnualCellKey } from '../utils/annualTimetable';
import { parseAnnualCellKey } from '../utils/annualTimetable';
import {
  collectClearedAnnualKeysByScopeFromSavedRows,
  parseGradeTermScopeKey,
} from '../utils/annualClearedSlotSync';
import {
  clearProgramProgressCellsCache,
  filterProgramProgressCellsByMondayIsoInclusive,
  loadProgramProgressCellsCache,
  mergeProgramProgressCellsCacheRows,
  saveProgramProgressCellsCache,
  type ProgramProgressCellsCacheCoverage,
} from '../utils/programProgressCellsCache';
import { notifySchoolProgramMutation } from '../utils/schoolDataCacheVersion';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import { textForStorage } from '../utils/programContentNullNormalize';
import {
  buildLearningContentScopeVersion,
  learningContentScopeVersionFromHead,
} from '../utils/learningContentCacheVersion';

const PROGRAM_PROGRESS_SELECT = [
  'id',
  'workspace_id',
  'grade_id',
  'academic_year',
  'term_number',
  'monday_iso',
  'weekday_key',
  'slot_index',
  'timetable_text',
  'unit_text',
  'learning_text',
  'learning_note',
  'presentation_ref_id',
  'worksheet_ref_id',
  'page_text',
  'assistant_page_text',
  'current_period_text',
  'total_period_text',
  'material_text',
  'record_cross_curricular_text',
  'record_memo_text',
  'record_evaluation_text',
  'record_class_text',
  'record_member_group_id',
  'bg_color_by_field',
  'created_at',
  'updated_at',
].join(', ');

/** PostgREST 기본 행 상한(1000) 초과 시에도 학기 전체 셀을 가져오기 위한 페이지 크기 */
const PROGRAM_PROGRESS_PAGE_SIZE = 1000;

const WEEKDAY_KEYS = new Set(['mon', 'tue', 'wed', 'thu', 'fri', 'sat']);
const MAX_PROGRAM_PROGRESS_SLOT_INDEX = 29;
const MONDAY_ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

function t(raw: string | null | undefined): string {
  return (raw ?? '').trim();
}

function naturalKeyForProgramProgressUpsert(row: ProgramProgressCellUpsert): string {
  return `${row.workspace_id}|${row.grade_id}|${row.academic_year}|${row.term_number}|${t(row.monday_iso)}|${t(row.weekday_key)}|${row.slot_index}`;
}

/** 동일 자연키 행이 한 번에 여러 개면 Postgres `ON CONFLICT DO UPDATE cannot affect row a second time` → PostgREST 500 */
function dedupeProgramProgressUpsertPayload(rows: ProgramProgressCellUpsert[]): ProgramProgressCellUpsert[] {
  const richness = (r: ProgramProgressCellUpsert) =>
    (r.learning_text ?? '').length + (r.unit_text ?? '').length + (r.timetable_text ?? '').length;
  const merged = new Map<string, ProgramProgressCellUpsert>();
  for (const row of rows) {
    const k = naturalKeyForProgramProgressUpsert(row);
    const prev = merged.get(k);
    if (!prev) {
      merged.set(k, row);
      continue;
    }
    const takeNew = richness(row) > richness(prev);
    const winner = takeNew ? row : prev;
    const loser = takeNew ? prev : row;
    merged.set(k, {
      ...winner,
      id: winner.id ?? loser.id ?? prev.id,
    });
  }
  return Array.from(merged.values());
}

/** DB 제약·FK 오류(500) 방지: 빈 record_member_group_id, 교시 범위 등 */
function sanitizeProgramProgressCellForUpsert(row: ProgramProgressCellUpsert): ProgramProgressCellUpsert {
  const memberGroupRaw = t(row.record_member_group_id ?? undefined);
  const memberGroup =
    !memberGroupRaw || memberGroupRaw === 'null' || memberGroupRaw === 'undefined' ? '' : memberGroupRaw;
  let slot = row.slot_index;
  if (!Number.isInteger(slot)) slot = 0;
  slot = Math.min(MAX_PROGRAM_PROGRESS_SLOT_INDEX, Math.max(0, slot));
  const wkRaw = t(row.weekday_key);
  const weekday_key = wkRaw as ProgramProgressCellUpsert['weekday_key'];
  return {
    ...row,
    monday_iso: t(row.monday_iso),
    weekday_key,
    slot_index: slot,
    timetable_text: textForStorage(row.timetable_text),
    unit_text: textForStorage(row.unit_text),
    learning_text: textForStorage(row.learning_text),
    page_text: textForStorage(row.page_text),
    assistant_page_text: textForStorage(row.assistant_page_text),
    current_period_text: textForStorage(row.current_period_text),
    total_period_text: textForStorage(row.total_period_text),
    material_text: textForStorage(row.material_text),
    record_cross_curricular_text: textForStorage(row.record_cross_curricular_text),
    record_memo_text: textForStorage(row.record_memo_text),
    record_evaluation_text: textForStorage(row.record_evaluation_text),
    record_class_text: textForStorage(row.record_class_text),
    record_member_group_id: memberGroup ? memberGroup : null,
    bg_color_by_field: row.bg_color_by_field ?? null,
  };
}

async function fetchAllProgramProgressRowsForGrade(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  mondayRange?: { from: string; to: string }
): Promise<ProgramProgressCell[]> {
  const rows: ProgramProgressCell[] = [];
  let offset = 0;
  for (;;) {
    let q = supabase
      .from('program_progress_cells')
      .select(PROGRAM_PROGRESS_SELECT)
      .eq('workspace_id', workspaceId)
      .eq('academic_year', academicYear)
      .eq('term_number', termNumber)
      .eq('grade_id', gradeId);
    if (mondayRange?.from && mondayRange?.to && MONDAY_ISO_RE.test(mondayRange.from) && MONDAY_ISO_RE.test(mondayRange.to)) {
      q = q.gte('monday_iso', mondayRange.from).lte('monday_iso', mondayRange.to);
    }
    const { data, error } = await q
      .order('monday_iso', { ascending: true })
      .order('weekday_key', { ascending: true })
      .order('slot_index', { ascending: true })
      .range(offset, offset + PROGRAM_PROGRESS_PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    const batch = (data ?? []) as unknown as ProgramProgressCell[];
    rows.push(...batch);
    if (batch.length < PROGRAM_PROGRESS_PAGE_SIZE) break;
    offset += PROGRAM_PROGRESS_PAGE_SIZE;
  }
  return rows;
}

export type GetProgramProgressCellsOptions = {
  /** true면 로컬 캐시를 무시하고 DB에서만 읽은 뒤 캐시를 갱신합니다(연간진도표·병합 로직 등). */
  forceRefresh?: boolean;
  /**
   * 둘 다 유효한 YYYY-MM-DD(월요일)이면 해당 구간만 조회합니다.
   * `forceRefresh`가 아니고 캐시 `coverage`가 전량(`full`)이면 head 일치 시 캐시에서 구간만 잘라 반환합니다.
   * 구간 조회 후에는 캐시에 병합 저장하며, 전량 캐시가 없을 때만 `coverage: partial`로 표시합니다.
   */
  mondayIsoFrom?: string;
  mondayIsoTo?: string;
};

/** 진도셀 head — `count:max(updated_at)` */
export async function fetchProgramProgressCellsHead(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): Promise<string> {
  if (!workspaceId || !gradeId) return '0:';
  const { data, error, count } = await supabase
    .from('program_progress_cells')
    .select('updated_at', { count: 'exact' })
    .eq('workspace_id', workspaceId)
    .eq('academic_year', academicYear)
    .eq('term_number', termNumber)
    .eq('grade_id', gradeId)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (error) throw new Error(error.message);
  const totalCount = count ?? 0;
  const maxUpdatedAt =
    totalCount > 0 && data?.[0]
      ? String((data[0] as { updated_at?: string }).updated_at ?? '')
      : '';
  return learningContentScopeVersionFromHead(totalCount, maxUpdatedAt);
}

async function isProgramProgressFullCacheFresh(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  disk: ReturnType<typeof loadProgramProgressCellsCache>
): Promise<boolean> {
  if (!disk.found || disk.coverage === 'partial' || !disk.contentVersion) return false;
  try {
    const head = await fetchProgramProgressCellsHead(workspaceId, academicYear, termNumber, gradeId);
    return head === disk.contentVersion;
  } catch {
    return false;
  }
}

export async function getProgramProgressCells(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  options?: GetProgramProgressCellsOptions
): Promise<ProgramProgressCell[]> {
  if (!workspaceId || !gradeId) return [];
  const disk = loadProgramProgressCellsCache(workspaceId, academicYear, termNumber, gradeId);
  const rangeFrom = (options?.mondayIsoFrom ?? '').trim();
  const rangeTo = (options?.mondayIsoTo ?? '').trim();
  const useRange =
    rangeFrom &&
    rangeTo &&
    MONDAY_ISO_RE.test(rangeFrom) &&
    MONDAY_ISO_RE.test(rangeTo) &&
    rangeFrom <= rangeTo;

  if (useRange) {
    if (!options?.forceRefresh && (await isProgramProgressFullCacheFresh(workspaceId, academicYear, termNumber, gradeId, disk))) {
      recordDevLocalStorageProgramCacheUse('진도셀(구간·head 일치)');
      return filterProgramProgressCellsByMondayIsoInclusive(disk.rows, rangeFrom, rangeTo);
    }
    if (!options?.forceRefresh && disk.found && disk.coverage === 'partial') {
      // partial은 head 없이 구간만 쓸 수 없어 네트워크
    }
    const fetched = await fetchAllProgramProgressRowsForGrade(workspaceId, academicYear, termNumber, gradeId, {
      from: rangeFrom,
      to: rangeTo,
    });
    const merged = disk.found ? mergeProgramProgressCellsCacheRows(disk.rows, fetched) : fetched;
    const nextCoverage: ProgramProgressCellsCacheCoverage =
      disk.found && disk.coverage === 'full' ? 'full' : 'partial';
    saveProgramProgressCellsCache(workspaceId, academicYear, termNumber, gradeId, merged, nextCoverage);
    return fetched;
  }

  if (!options?.forceRefresh && (await isProgramProgressFullCacheFresh(workspaceId, academicYear, termNumber, gradeId, disk))) {
    recordDevLocalStorageProgramCacheUse('진도셀(전량·head 일치)');
    return disk.rows;
  }
  const rows = await fetchAllProgramProgressRowsForGrade(workspaceId, academicYear, termNumber, gradeId);
  saveProgramProgressCellsCache(workspaceId, academicYear, termNumber, gradeId, rows, 'full', {
    contentVersion: buildLearningContentScopeVersion(rows),
  });
  return rows;
}

/** 자연키 목록으로만 program_progress_cells 조회(학기 전체 select 회피) */
export type ProgramProgressNaturalKey = {
  gradeId: string;
  mondayIso: string;
  weekdayKey: string;
  slotIndex: number;
};

const NATURAL_KEY_OR_CHUNK = 18;

export async function getProgramProgressCellsForNaturalKeys(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  keys: ProgramProgressNaturalKey[]
): Promise<ProgramProgressCell[]> {
  if (!workspaceId || !gradeId || !keys.length) return [];
  const seen = new Set<string>();
  const unique: ProgramProgressNaturalKey[] = [];
  keys.forEach((k) => {
    const wk = t(k.weekdayKey);
    const mon = t(k.mondayIso);
    if (!WEEKDAY_KEYS.has(wk) || !MONDAY_ISO_RE.test(mon)) return;
    if (!Number.isInteger(k.slotIndex)) return;
    const sk = `${k.gradeId}|${mon}|${wk}|${k.slotIndex}`;
    if (seen.has(sk)) return;
    seen.add(sk);
    unique.push({ gradeId, mondayIso: mon, weekdayKey: wk, slotIndex: k.slotIndex });
  });
  const out: ProgramProgressCell[] = [];
  for (let i = 0; i < unique.length; i += NATURAL_KEY_OR_CHUNK) {
    const chunk = unique.slice(i, i + NATURAL_KEY_OR_CHUNK);
    const orFilter = chunk
      .map((k) => `and(monday_iso.eq.${k.mondayIso},weekday_key.eq.${k.weekdayKey},slot_index.eq.${k.slotIndex})`)
      .join(',');
    const { data, error } = await supabase
      .from('program_progress_cells')
      .select(PROGRAM_PROGRESS_SELECT)
      .eq('workspace_id', workspaceId)
      .eq('academic_year', academicYear)
      .eq('term_number', termNumber)
      .eq('grade_id', gradeId)
      .or(orFilter);
    if (error) throw new Error(error.message);
    out.push(...(((data ?? []) as unknown) as ProgramProgressCell[]));
  }
  return out;
}

export async function upsertProgramProgressCells(
  rows: ProgramProgressCellUpsert[],
  options?: ProgramMutationNotifyOptions
): Promise<ProgramProgressCell[]> {
  if (!rows.length) return [];
  const validRows = rows.filter(
    (row) =>
      !!row.workspace_id &&
      !!row.grade_id &&
      !!row.monday_iso &&
      MONDAY_ISO_RE.test(String(row.monday_iso).trim()) &&
      Number.isFinite(row.academic_year as number) &&
      WEEKDAY_KEYS.has(String(row.weekday_key ?? '').trim()) &&
      Number.isInteger(row.slot_index) &&
      row.slot_index >= 0 &&
      row.slot_index <= MAX_PROGRAM_PROGRESS_SLOT_INDEX &&
      (row.term_number === 1 || row.term_number === 2)
  );
  if (!validRows.length) return [];
  const sanitized = validRows.map((row) => {
    const { id: _ignoredId, ...rest } = sanitizeProgramProgressCellForUpsert(row);
    return rest;
  });
  // 동일 (school, grade, 주·요일·교시) 행이 여러 개면 Postgres upsert 한 문장에서 동일 행을 두 번 갱신하려 해 500이 난다.
  const deduped = dedupeProgramProgressUpsertPayload(sanitized as ProgramProgressCellUpsert[]);
  const payload = deduped.map((rest) => ({
    ...rest,
    updated_at: new Date().toISOString(),
  }));
  const { data, error } = await supabase
    .from('program_progress_cells')
    .upsert(payload, {
      onConflict: 'workspace_id,grade_id,academic_year,term_number,monday_iso,weekday_key,slot_index',
    })
    .select(PROGRAM_PROGRESS_SELECT);
  if (error) {
    const detail = [error.message, error.details, error.hint, error.code].filter(Boolean).join(' | ');
    throw new Error(detail || 'program_progress_cells 저장 실패');
  }
  const savedRows = (data ?? []) as unknown as ProgramProgressCell[];
  const first = savedRows[0] ?? deduped[0] ?? validRows[0];
  const workspaceId = first?.workspace_id ?? '';
  const ay = first?.academic_year;
  const tn = first?.term_number;
  const gid = first?.grade_id;
  if (workspaceId && ay != null && tn != null && gid) {
    const cached = loadProgramProgressCellsCache(workspaceId, ay, tn as 1 | 2, gid);
    if (cached.found) {
      saveProgramProgressCellsCache(
        workspaceId,
        ay,
        tn as 1 | 2,
        gid,
        mergeProgramProgressCellsCacheRows(cached.rows, savedRows),
        cached.coverage
      );
    } else {
      clearProgramProgressCellsCache(workspaceId, ay, tn as 1 | 2, gid);
    }
  }
  if (!options?.deferSchoolVersionNotify && workspaceId) {
    await notifySchoolProgramMutation(workspaceId);
  }
  return savedRows;
}

export async function deleteProgramProgressCellsByIds(
  ids: string[],
  options?: ProgramMutationNotifyOptions
): Promise<void> {
  const targetIds = ids.map((v) => v.trim()).filter(Boolean);
  if (!targetIds.length) return;
  const { data: rowsToDelete } = await supabase
    .from('program_progress_cells')
    .select('id, workspace_id, academic_year, term_number, grade_id')
    .in('id', targetIds);
  const { error } = await supabase.from('program_progress_cells').delete().in('id', targetIds);
  if (error) throw new Error(error.message);
  const deletedRows = ((rowsToDelete ?? []) as Array<{
    id: string;
    workspace_id: string;
    academic_year: number;
    term_number: 1 | 2;
    grade_id: string;
  }>).filter((row) => !!row.id && !!row.workspace_id && !!row.grade_id);
  const workspaceId = deletedRows[0]?.workspace_id ?? '';
  const grouped = new Map<string, { workspaceId: string; academicYear: number; termNumber: 1 | 2; gradeId: string }>();
  deletedRows.forEach((row) => {
    const key = `${row.workspace_id}|${row.academic_year}|${row.term_number}|${row.grade_id}`;
    if (!grouped.has(key)) {
      grouped.set(key, {
        workspaceId: row.workspace_id,
        academicYear: row.academic_year,
        termNumber: row.term_number,
        gradeId: row.grade_id,
      });
    }
  });
  grouped.forEach(({ workspaceId: sid, academicYear, termNumber, gradeId }) => {
    const cached = loadProgramProgressCellsCache(sid, academicYear, termNumber, gradeId);
    if (cached.found) {
      const ids = new Set(
        deletedRows
          .filter(
            (row) =>
              row.workspace_id === sid &&
              row.academic_year === academicYear &&
              row.term_number === termNumber &&
              row.grade_id === gradeId
          )
          .map((row) => row.id)
      );
      saveProgramProgressCellsCache(
        sid,
        academicYear,
        termNumber,
        gradeId,
        cached.rows.filter((row) => !ids.has(row.id)),
        cached.coverage
      );
    } else {
      clearProgramProgressCellsCache(sid, academicYear, termNumber, gradeId);
    }
  });
  if (!options?.deferSchoolVersionNotify && workspaceId) {
    await notifySchoolProgramMutation(workspaceId);
  }
}

export async function deleteProgramProgressCellsForAnnualKeys(
  workspaceId: string,
  gradeId: string,
  annualCellKeys: string[],
  options?: ProgramMutationNotifyOptions
): Promise<void> {
  const keys = annualCellKeys
    .map((key) => parseAnnualCellKey(key.trim()))
    .filter(
      (parsed): parsed is NonNullable<ReturnType<typeof parseAnnualCellKey>> =>
        parsed != null && parsed.gradeId === gradeId
    );
  if (!workspaceId.trim() || !gradeId.trim() || keys.length === 0) return;

  const ids: string[] = [];
  const byTerm = new Map<string, { academicYear: number; termNumber: 1 | 2; keys: typeof keys }>();
  keys.forEach((key) => {
    const academicYear = Number(key.mondayIso.slice(0, 4));
    if (!Number.isInteger(academicYear)) return;
    // 연간 셀 키 자체에는 학기 정보가 없어 두 학기 모두 조회한다.
    ([1, 2] as const).forEach((termNumber) => {
      const groupKey = `${academicYear}:${termNumber}`;
      const group = byTerm.get(groupKey) ?? { academicYear, termNumber, keys: [] as typeof keys };
      group.keys.push(key);
      byTerm.set(groupKey, group);
    });
  });

  for (const { academicYear, termNumber, keys: termKeys } of Array.from(byTerm.values())) {
    const cells = await getProgramProgressCellsForNaturalKeys(
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      termKeys.map((key) => ({
        gradeId,
        mondayIso: key.mondayIso,
        weekdayKey: key.weekdayKey,
        slotIndex: key.slotIndex,
      }))
    );
    cells.forEach((cell) => {
      if (cell.id) ids.push(cell.id);
    });
  }

  await deleteProgramProgressCellsByIds(Array.from(new Set(ids)), options);
}

/**
 * 연간 시간표 저장본 중 과목명·색이 모두 비어 있는 칸에 대해,
 * 해당 주차·교시 좌표의 program_progress_cells 행을 DB에서 제거한다.
 * @returns 실제로 삭제 대상이 있었던 학년·학기(이벤트 발행용)
 */
export async function deleteProgramProgressCellsForClearedAnnualSlots(
  savedRows: AnnualTimetableCell[]
): Promise<{ workspaceId: string; academicYear: number; termNumber: 1 | 2; gradeId: string }[]> {
  if (!savedRows.length) return [];
  type ScopeKey = string;
  const emptyAnnualKeysByScope = collectClearedAnnualKeysByScopeFromSavedRows(savedRows);
  const deleteIds: string[] = [];
  const scopesWithDeletes = new Set<ScopeKey>();
  for (const [scopeKey, annualKeys] of Array.from(emptyAnnualKeysByScope.entries())) {
    if (!annualKeys.size) continue;
    const parsed = parseGradeTermScopeKey(scopeKey);
    if (!parsed) continue;
    const { workspaceId, academicYear, termNumber, gradeId } = parsed;
    const cells = await getProgramProgressCells(workspaceId, academicYear, termNumber, gradeId, {
      forceRefresh: true,
    });
    for (const c of cells) {
      const k = buildAnnualCellKey(c.grade_id, c.monday_iso, c.weekday_key, c.slot_index);
      if (annualKeys.has(k) && c.id) {
        deleteIds.push(c.id);
        scopesWithDeletes.add(scopeKey);
      }
    }
  }
  await deleteProgramProgressCellsByIds(deleteIds);
  return Array.from(scopesWithDeletes)
    .map((sk) => parseGradeTermScopeKey(sk))
    .filter((v): v is { workspaceId: string; academicYear: number; termNumber: 1 | 2; gradeId: string } => v != null);
}

function progressCellToClearedFixedLeftoverUpsert(cell: ProgramProgressCell): ProgramProgressCellUpsert {
  return {
    id: cell.id,
    workspace_id: cell.workspace_id,
    grade_id: cell.grade_id,
    academic_year: cell.academic_year,
    term_number: cell.term_number,
    monday_iso: cell.monday_iso,
    weekday_key: cell.weekday_key,
    slot_index: cell.slot_index,
    timetable_text: cell.timetable_text,
    unit_text: '',
    learning_text: '',
    learning_note: '',
    presentation_ref_id: null,
    worksheet_ref_id: null,
    page_text: '',
    assistant_page_text: '',
    current_period_text: '',
    total_period_text: '',
    material_text: '',
    record_cross_curricular_text: cell.record_cross_curricular_text,
    record_memo_text: cell.record_memo_text,
    record_evaluation_text: cell.record_evaluation_text,
    record_class_text: cell.record_class_text,
    record_member_group_id: cell.record_member_group_id ?? null,
    bg_color_by_field: cell.bg_color_by_field ?? {},
  };
}

function fixedRowToUnbindUpsert(row: ProgramFixedContentRow) {
  return {
    id: row.id,
    workspace_id: row.workspace_id,
    grade_id: row.grade_id,
    academic_year: row.academic_year,
    term_number: row.term_number,
    order_index: row.order_index,
    source_annual_cell_key: null,
    event_name: row.event_name ?? '',
    timetable_text: row.timetable_text,
    unit_text: row.unit_text,
    learning_text: row.learning_text,
    learning_note: row.learning_note ?? '',
    presentation_ref_id: row.presentation_ref_id ?? null,
    worksheet_ref_id: row.worksheet_ref_id ?? null,
    page_text: row.page_text,
    assistant_page_text: row.assistant_page_text,
    current_period_text: row.current_period_text,
    total_period_text: row.total_period_text,
    material_text: row.material_text,
    record_cross_curricular_text: row.record_cross_curricular_text,
    record_memo_text: row.record_memo_text,
    record_evaluation_text: row.record_evaluation_text,
    record_class_text: row.record_class_text,
    record_member_group_id: row.record_member_group_id ?? null,
    bg_color_by_field: row.bg_color_by_field ?? {},
  };
}

/**
 * 고정 팔레트 색이 아닌 칸에 남은 ◆시드·고정행 연결을 지운다.
 * 수업·평가 기록이 있는 진도셀은 본문만 비우고 행은 남긴다.
 */
export async function applyFixedProgramUnfixedSlotCleanup(input: {
  stillFixedAnnualKeys: ReadonlySet<string>;
  /** 행사 색이 아닌 시간표로 바뀐 칸 — 묶인 행사 내용 행을 삭제 */
  reassignedAnnualKeys?: ReadonlySet<string>;
  existingProgramCells: ProgramProgressCell[];
  existingFixedRows: ProgramFixedContentRow[];
  options?: ProgramMutationNotifyOptions;
}): Promise<boolean> {
  const plan = planFixedProgramUnfixedSlotCleanup(
    input.stillFixedAnnualKeys,
    input.existingProgramCells,
    input.existingFixedRows,
    input.reassignedAnnualKeys
  );
  if (!cleanupPlanHasWork(plan)) return false;
  const notify = input.options;
  await Promise.all([
    plan.progressCellIdsToDelete.length
      ? deleteProgramProgressCellsByIds(plan.progressCellIdsToDelete, notify)
      : Promise.resolve(),
    plan.progressCellsToClear.length
      ? upsertProgramProgressCells(plan.progressCellsToClear.map(progressCellToClearedFixedLeftoverUpsert), notify)
      : Promise.resolve(),
    plan.fixedRowIdsToDelete.length
      ? deleteProgramFixedContentRowsByIds(plan.fixedRowIdsToDelete, notify)
      : Promise.resolve(),
    plan.fixedRowsToUnbind.length
      ? upsertProgramFixedContentRows(plan.fixedRowsToUnbind.map(fixedRowToUnbindUpsert), notify)
      : Promise.resolve(),
  ]);
  return true;
}
